/**
 * Metric catalogue for the analysis drawer.
 *
 * The plottable metrics are columns on the daily series endpoint. The rest are
 * listed but disabled, with the reason stated in the UI — Market Share needs a market scope (geo + level)
 * and the SOV family is keyed on KEYWORDS, which the Availability page has no
 * context for. Showing them as flat or fabricated lines would be worse than
 * showing them as unavailable.
 */
export type MetricKey =
  | 'sales' | 'wt_osa' | 'listing' | 'units' | 'inventory' | 'stores_present'
  | 'market_share' | 'assortment' | 'overall_sov' | 'ad_sov' | 'organic_sov';

export interface MetricDef {
  key: MetricKey;
  label: string;
  /** Column on the daily series response, when it is plottable here. */
  field?: string;
  unit: 'percent' | 'currency' | 'count';
  color: string;
  available: boolean;
  /** Why it cannot be plotted on this screen. */
  reason?: string;
}

export const METRICS: MetricDef[] = [
  { key: 'sales', label: 'Sales', field: 'sales', unit: 'currency', color: '#7c5cff', available: true },
  { key: 'wt_osa', label: 'Wt OSA%', field: 'wt_osa', unit: 'percent', color: '#e0489d', available: true },
  // Plottable here, unlike on the table: the daily line is stores_present over
  // city_live_stores per day, which is a different measure from the 14-day
  // rolling Listing% the table reports. They will not agree, and should not.
  { key: 'listing', label: 'Listing%', field: 'listing', unit: 'percent', color: '#2f9be0', available: true },
  { key: 'units', label: 'Units', field: 'units', unit: 'count', color: '#f5a524', available: true },
  { key: 'inventory', label: 'Inventory', field: 'inventory', unit: 'count', color: '#12a594', available: true },
  { key: 'stores_present', label: 'Stores Present', field: 'stores_present', unit: 'count', color: '#8b8fa3', available: true },
  {
    key: 'market_share', label: 'Market Share', unit: 'percent', color: '#3d7dff', available: false,
    reason: 'Market share needs a market scope (geo + category level); it is not defined at this grain.',
  },
  {
    key: 'assortment', label: 'Assortment', unit: 'count', color: '#6b7280', available: false,
    reason: 'Assortment is a distinct-SKU count, not a daily series on this resource.',
  },
  {
    key: 'overall_sov', label: 'Overall SOV', unit: 'percent', color: '#9333ea', available: false,
    reason: 'Share of voice is keyed on search keywords, which this screen has no context for.',
  },
  {
    key: 'ad_sov', label: 'Ad SOV', unit: 'percent', color: '#db2777', available: false,
    reason: 'Share of voice is keyed on search keywords, which this screen has no context for.',
  },
  {
    key: 'organic_sov', label: 'Organic SOV', unit: 'percent', color: '#0891b2', available: false,
    reason: 'Share of voice is keyed on search keywords, which this screen has no context for.',
  },
];

export const METRIC_BY_KEY = new Map(METRICS.map((m) => [m.key, m]));

export const formatMetric = (m: MetricDef, v: number): string => {
  if (m.unit === 'percent') return `${v.toFixed(1)}%`;
  if (m.unit === 'currency') {
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} lac`;
    if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
  }
  return Math.round(v).toLocaleString('en-IN');
};
